import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { TokenStorageService } from './token-storage.service';
import {
  AuthResponse,
  ChangePasswordRequest,
  LoginRequest,
  RefreshTokenRequest,
  ResetPasswordRequest,
  VerifyOtpRequest
} from '../models/auth.models';
import { UserDto } from '../models/user.models';

/**
 * Autenticación y sesión del usuario administrador.
 * Endpoints: IdentityService.Api/Controllers/v1/AuthenticationController.cs
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly baseUrl = `${environment.apiUrl}/authentication`;

  /** Usuario autenticado actual (null si no hay sesión). Se hidrata desde localStorage al arrancar. */
  readonly currentUser = signal<UserDto | null>(this.tokenStorage.getUser());

  readonly isAuthenticated = computed(
    () => this.currentUser() !== null && !!this.tokenStorage.getAccessToken()
  );

  /** POST /authentication/sign-in */
  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/sign-in`, credentials).pipe(
      tap((response) => this.persistSession(response))
    );
  }

  /**
   * POST /authentication/refresh
   * Usado principalmente por el AuthInterceptor ante un 401, pero se expone
   * público por si algún componente necesita forzar una renovación.
   */
  refreshToken(payload: RefreshTokenRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/refresh`, payload).pipe(
      tap((response) => this.persistSession(response))
    );
  }

  /** POST /authentication/change-password */
  changePassword(payload: ChangePasswordRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/change-password`, payload);
  }

  /** GET /authentication/forgot-password/{email} */
  forgotPassword(email: string): Observable<{ success: boolean; message: string }> {
    return this.http.get<{ success: boolean; message: string }>(
      `${this.baseUrl}/forgot-password/${encodeURIComponent(email)}`
    );
  }

  /** POST /authentication/verify-otp */
  verifyOtp(payload: VerifyOtpRequest): Observable<{ resetKey: string }> {
    return this.http.post<{ resetKey: string }>(`${this.baseUrl}/verify-otp`, payload);
  }

  /** POST /authentication/reset-password */
  resetPassword(payload: ResetPasswordRequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.baseUrl}/reset-password`, payload);
  }

  /** Cierra la sesión local (no invalida el refresh token en el backend: no existe endpoint de logout). */
  logout(): void {
    this.tokenStorage.clear();
    this.currentUser.set(null);
  }

  private persistSession(response: AuthResponse): void {
    this.tokenStorage.setSession(response);
    this.currentUser.set(response.user);
  }
}
